import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppShell,
  Group,
  Title,
  ActionIcon,
  Tooltip,
  Box,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBooks,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
} from '@tabler/icons-react';

interface PlayerLayoutProps {
  children: ReactNode;
  title?: string;
  /** Optional middle slot (breadcrumb, session counter etc.). */
  headerCenter?: ReactNode;
  /** Optional right-side slot for header actions (after the rail toggle). */
  headerActions?: ReactNode;
  /**
   * Override the back-button destination. Defaults to <c>navigate(-1)</c>
   * (history pop), but the session player passes the course detail URL so
   * the back arrow always lands on the course page rather than the previous
   * session the viewer was watching.
   */
  backHref?: string;
  /**
   * Optional right-side rail (curriculum / overview / notes). When provided
   * AND <c>railOpen</c> is true, the layout reserves room for it and renders
   * a collapse/expand toggle in the header.
   */
  rail?: ReactNode;
  railOpen?: boolean;
  onToggleRail?: () => void;
  /** Width of the rail panel when open (default: 360px). */
  railWidth?: number;
  /** When true, the side rail is hidden and the toggle is suppressed. Use on
   *  mobile where a bottom drawer takes its place. */
  hideRail?: boolean;
}

/**
 * Immersive player layout — minimal chrome plus an optional collapsible
 * right-side rail. The main player area resizes automatically when the rail
 * opens or closes (CSS grid, no JS measuring).
 */
export function PlayerLayout({
  children,
  title,
  headerCenter,
  headerActions,
  rail,
  railOpen = false,
  onToggleRail,
  railWidth = 360,
  hideRail = false,
  backHref,
}: PlayerLayoutProps) {
  const navigate = useNavigate();
  const showRail = !!rail && railOpen && !hideRail;
  const handleBack = () => {
    if (backHref) navigate(backHref);
    else navigate(-1);
  };

  return (
    <AppShell header={{ height: 56 }} padding={0}>
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" gap="md">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: '0 0 auto' }}>
            <Tooltip label="Back to course">
              <ActionIcon variant="subtle" onClick={handleBack} aria-label="Back to course">
                <IconArrowLeft size={18} />
              </ActionIcon>
            </Tooltip>
            <IconBooks size={20} color="var(--mantine-color-brand-6)" />
            <Title
              order={6}
              lh={1}
              fw={600}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 280,
              }}
            >
              {title ?? 'Acts Resource Centre'}
            </Title>
          </Group>

          {/* Center slot — breadcrumb / session counter / progress. Hidden on
              very small screens to keep the header from wrapping. */}
          {headerCenter && (
            <Box style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }} visibleFrom="sm">
              {headerCenter}
            </Box>
          )}

          <Group gap="xs" wrap="nowrap" style={{ flex: '0 0 auto' }}>
            {headerActions}
            {rail && onToggleRail && !hideRail && (
              <Tooltip label={railOpen ? 'Hide curriculum' : 'Show curriculum'}>
                <ActionIcon
                  variant="subtle"
                  onClick={onToggleRail}
                  aria-label={railOpen ? 'Hide curriculum' : 'Show curriculum'}
                >
                  {railOpen ? (
                    <IconLayoutSidebarRightCollapse size={18} />
                  ) : (
                    <IconLayoutSidebarRightExpand size={18} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: showRail ? `1fr ${railWidth}px` : '1fr',
            transition: 'grid-template-columns 220ms ease',
            minHeight: 'calc(100vh - 56px)',
          }}
        >
          <Box style={{ minWidth: 0 }}>{children}</Box>
          {showRail && (
            <Box
              style={{
                borderLeft: '1px solid var(--mantine-color-default-border)',
                background: 'var(--mantine-color-body)',
                position: 'sticky',
                top: 56,
                height: 'calc(100vh - 56px)',
                overflow: 'hidden',
              }}
            >
              {rail}
            </Box>
          )}
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
